// deps
import React, { memo } from "react";

// internal-deps
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import type { IconName } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb/types";

// interfaces
import type { ISplitBillWarningCallout } from "@dineout-payment-success-ui/interfaces";

// constants
import { SPLIT_BILL_OUTCOME_IMAGE_SIZE, SPLIT_BILL_OUTCOME_SHIMMER } from "@dineout-payment-success-ui/constants";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { isEmpty } from "@swiggy-private/js-utils";

// styles
import {
    StatusWrapper,
    StatusImageWrap,
    StatusMessageBlock,
    StatusWarningCallout,
    StatusWarningCalloutIconWrap,
    StatusWarningCalloutText,
    StatusCtaWrap,
    StatusShimmerSubtextGroup,
    ShimmerContainer,
} from "./styled";

interface ISplitBillStatusViewProps {
    imageId: string;
    title: string;
    subTitle: string;
    ctaLabel: string;
    warningCallout?: ISplitBillWarningCallout;
    onCta: () => void;
}

const SplitBillStatusView: React.FC<ISplitBillStatusViewProps> = ({
    imageId,
    title,
    subTitle,
    ctaLabel,
    warningCallout,
    onCta,
}) => {
    const activeWarningCallout = isEmpty(warningCallout) ? undefined : warningCallout;

    return (
        <StatusWrapper data-testid={SPLIT_BILL_TEST_IDS.STATUS}>
            <StatusImageWrap>
                <Image
                    cloudinaryId={imageId}
                    width={SPLIT_BILL_OUTCOME_IMAGE_SIZE}
                    height={SPLIT_BILL_OUTCOME_IMAGE_SIZE}
                />
            </StatusImageWrap>
            <StatusMessageBlock hasWarningCallout={Boolean(activeWarningCallout)}>
                <Text variant="V2.subheading1.neutral.bold" color="text_Highest_Emphasis">
                    {title}
                </Text>
                <Text variant="V2.body2.neutral.regular" color="text_Med_Emphasis">
                    {subTitle}
                </Text>
            </StatusMessageBlock>
            {activeWarningCallout && (
                <StatusWarningCallout
                    backgroundColor={activeWarningCallout.backgroundColor}
                    data-testid={SPLIT_BILL_TEST_IDS.WARNING_CALLOUT}
                >
                    <StatusWarningCalloutIconWrap>
                        <SpriteIconWeb
                            iconName={activeWarningCallout.iconId as IconName}
                            fillColor={activeWarningCallout.iconColor}
                            strokeColor={activeWarningCallout.iconColor}
                            aria-hidden
                        />
                    </StatusWarningCalloutIconWrap>
                    <StatusWarningCalloutText variant="V2.body3.neutral.regular" color={activeWarningCallout.textColor}>
                        {activeWarningCallout.text}
                    </StatusWarningCalloutText>
                </StatusWarningCallout>
            )}
            <StatusCtaWrap>
                <SuperButton
                    variant="fullwidth"
                    buttonType="primary"
                    buttonSize="large"
                    onClick={onCta}
                    data-testid={SPLIT_BILL_TEST_IDS.STATUS_CTA}
                >
                    {ctaLabel}
                </SuperButton>
            </StatusCtaWrap>
        </StatusWrapper>
    );
};

function SplitBillStatusShimmerView() {
    return (
        <StatusWrapper>
            <StatusImageWrap>
                <ShimmerContainer {...SPLIT_BILL_OUTCOME_SHIMMER.image} />
            </StatusImageWrap>
            <StatusMessageBlock>
                <ShimmerContainer {...SPLIT_BILL_OUTCOME_SHIMMER.titleLine} />
                <StatusShimmerSubtextGroup>
                    <ShimmerContainer {...SPLIT_BILL_OUTCOME_SHIMMER.subtextLine1} />
                    <ShimmerContainer {...SPLIT_BILL_OUTCOME_SHIMMER.subtextLine2} />
                </StatusShimmerSubtextGroup>
            </StatusMessageBlock>
            <StatusCtaWrap>
                <ShimmerContainer {...SPLIT_BILL_OUTCOME_SHIMMER.cta} />
            </StatusCtaWrap>
        </StatusWrapper>
    );
}

export const SplitBillStatusShimmer = memo(SplitBillStatusShimmerView);

export default memo(SplitBillStatusView);
